import React from 'react';
import { useNavigate } from "react-router-dom";
import { SpheraButtonEx } from "@sphera-cloud/component-ui-reactjs";
import { Layout } from '../components/MasterPage/Layout';
import { LoadingPanel } from '../components/LoadingPanel';
import { UserForm } from '../components/UserForm';
import useFetchWithMsal from '../services/useFetchWithMsal';
import { apiRoutes } from '../services/apiRoutes';
import { SiteData } from '../Data/SiteData';

// Maps the user's assigned public sites onto the site dropdown options, dropping any that no longer
// resolve to an option. Kept at module scope so it doesn't add nesting depth inside getData's promise chain.
const mapUserPortals = (publicSiteUsers, siteOptions) => {
	if (!publicSiteUsers)
		return [];
	return publicSiteUsers
		.map(x => siteOptions.find(y => x.publicSiteId === y.value))
		.filter(Boolean);
};

export function ManageUser(props) {
	const usertypes = {
		SpheraAdmin: 1,
		TenentAdmin: 2,
		Tenent: 3,
		Portal: 4
	};
	const navigate = useNavigate();
	const hrefParts = window.location.href.split("/");
	const userId = hrefParts[hrefParts.length - 1];
	const [userRoles, setuserRoles] = React.useState([]);
	const [currentUser, setcurrentUser] = React.useState({});
	const [sites, setsites] = React.useState([]);
	const [sitesSelected, setsitesSelected] = React.useState([]);
	const [pending, setPending] = React.useState(false);
	const [success, setsuccess] = React.useState(false);
	const [error, seterror] = React.useState(false);
	const [errorMessage, seterrorMessage] = React.useState("");

	const [userNameValid, setuserNameValid] = React.useState(true);
	const [disableSave, setdisableSave] = React.useState(false);

	const { execute, result } = useFetchWithMsal({
		scopes: props.loginConfig.protectedResources.apiDocumentList.scopes.read,
	});
	const { getSites } = SiteData({ execute: execute, customer: props.customer });

	const getData = () => {
		setPending(true);
		execute("GET", apiRoutes.users.getUser + `/` + userId)
			.then(json => {
				if (json != null) {
					if (json.user == null)
						json.user = { status: 1, userName: "" };
					if (json.user.userRoleId == null)
						json.user.userRoleId = 0;
					json.roles.unshift({
						userRoleId: 0,
						roleName: props.localizations.SelectRole ?? "-- Select a User Role --"
					});
					if (json.user.phoneNumber == null)
						json.user.phoneNumber = "";
					setuserRoles(json.roles);
					setcurrentUser(json.user);
					let userType = (window.location.pathname.indexOf('/manageUser') > -1) ? usertypes.Tenent : usertypes.Portal;
					if (userType == usertypes.Portal) {
						getSites().then((data) => {
							setsites(data);
							setsitesSelected(mapUserPortals(json.user.publicSiteUsers, data));
							setPending(false);
						});
					}
					else {
						setPending(false);
					}
				}
			}).catch(err => {
				seterrorMessage(err);
				seterror(true);
			});
	}
	const assignUserToSites = async (sitesSelected, userId) => {
		let siteIds = sitesSelected.map(x => x.value).join(",");
		if (siteIds === "")
			siteIds = null;
		await execute("POST", apiRoutes.users.AssignUserToSites(userId), siteIds);
	}
	const handleSitesDropdownChange = (e) => {
		let newsites = sites;
		newsites.forEach(x => {
			x.show = false;
		});
		e.value.forEach(x => {
			let site = newsites.find(y => x.value === y.value);
			if (site)
				site.show = true;
		});

		let selecSites = e.value;
		setsitesSelected(selecSites);
		setsites(newsites);
	};

	const validateUser = () => {
		setuserNameValid(Boolean(currentUser.userName));
		return Boolean(currentUser.userName);
	}

	const handleSubmission = () => {
		if (validateUser()) {
			setdisableSave(true);
			setPending(true);
			currentUser.phoneNumberWithCode = "";
			currentUser.firstName = "";
			currentUser.lastName = "";
			currentUser.externalUserId = "";

			if (currentUser.userType == null)
				currentUser.userType = (window.location.pathname.indexOf('/manageUser') > -1) ? usertypes.Tenent : usertypes.Portal;

			currentUser.customerId = props.currentTenent.CustomerId;
			if (currentUser.userId == null)
				currentUser.userId = 0;

			execute("POST", apiRoutes.users.post, currentUser)
				.then(async json => {
					if (json != null) {
						setdisableSave(false);
						if (json.status === 401)
							navigate("/");

						if (json > 0) {
							// Await the assignment before navigating: firing navigate() alongside an unawaited
							// promise meant a slow or failed call was abandoned mid-flight, and the failure
							// surfaced on a component the user had already left.
							try {
								await assignUserToSites(sitesSelected, json);
							}
							catch {
								seterror(true);
								seterrorMessage(props.localizations.RetrieveSiteError);
								setPending(false);
								return;
							}
							setsuccess(true);
							setPending(false);
							setTimeout(function () { navigate((window.location.pathname.indexOf('/manageUser') > -1) ? "/manageUsers" : "/manageCpUsers"); }, 1500);
						}
						else {
							seterrorMessage(props.localizations.UserNameNotUnique);
							seterror(true);
							setPending(false);
						}
					}
				})
				.catch(err => {
					seterrorMessage(err);
					seterror(true);
					setPending(false);
				});
		}
		else {
			seterrorMessage(props.localizations.NotAllFieldsValid);
			seterror(true);
		}
	};
	
	const [gotData, setgotData] = React.useState(false);
	React.useEffect(() => {
		if (gotData || result == null)
			return;
		setgotData(true);
	}, [execute]);

	React.useEffect(() => {
		if (gotData)
			getData();
	}, [gotData]);

	return (
		<Layout versionNumber={props.versionNumber} currentTenent={props.currentTenent} success={success} setSuccess={setsuccess} error={error} setError={seterror} errorMessage={errorMessage} localizations={props.localizations} changeLanguage={props.changeLanguage} languageValue={props.languageValue} >
			<UserForm currentUser={currentUser} setcurrentUser={setcurrentUser} CustomerWizard={false} IsIaUser={(window.location.pathname.indexOf('/manageUser') > -1)}
				localizations={props.localizations} userNameValid={userNameValid} handleSitesDropdownChange={handleSitesDropdownChange} sites={sites} sitesSelected={sitesSelected}
				setuserNameValid={setuserNameValid} />

			{pending && <LoadingPanel localizations={props.localizations} />}

			<div className="row buttonDiv" style={{ width: "600px", paddingTop: "25px" }}>
				<div className="col-1">
					<SpheraButtonEx text={props.localizations.Cancel} onClick={() => navigate((window.location.pathname.indexOf('/manageUser') > -1) ? "/manageUsers" : "/manageCpUsers")} />
				</div>
				<div className="col-1" style={{ paddingLeft: "312px"}}>
					<SpheraButtonEx text={props.localizations.Save} primary onClick={handleSubmission} disabled={disableSave} />
				</div>
			</div>
		</Layout>
	);
}
